import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { UserRole } from '@data-access/roles';
import { submissionDetailTokenizer } from '@data-access/submissions/store';
import {
  BasePermission,
  PermissionGrants,
  QuestionnaireAccess,
  ReassignSubmission,
  SiteSubmission,
  StudySubmission,
  SubmissionDocumentAccess,
  SubmissionSuperAdmin,
  UpdateSubmissionPriority,
  UpdateSubmissionTeam,
} from '@shared/interfaces/permissions.interface';
import { convertKeysToCamelCase, convertSnakeToCamelCase } from '@shared/utils';
import { AuthService } from '@auth/auth.service';
import { SubmissionResponse, UpdateSubmissionStagePayload } from '../types';
import environment from '@environments/environment';
import { PermissionsService } from '@shared/services/permissions.service';
import { combineLatest, map } from 'rxjs';
import {
  DIALOG_TYPE,
  OpenDialogState,
  WcgSingleTextDialogComponent,
} from '@data-access/shared';
import { DIALOG_MODES } from '@erm/cluster';
import { DialogSize } from '@wcg/frontend-framework';
import { Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { SubmissionSite } from '@data-access/sites';
@Injectable()
export class SubmissionDetailFacadeService {
  /** Injected Services */
  private readonly _submissionDetailStore = submissionDetailTokenizer();
  private readonly _authService = inject(AuthService);
  private readonly _permissionsService = inject(PermissionsService);
  private readonly _destroyRef = inject(DestroyRef);
  /** Injected Services */

  /** Signals */
  /** The ID of the current submission */
  $submissionId = this._submissionDetailStore.$submissionId;
  /** The current stage of the submission */
  $currentStage = this._submissionDetailStore.$current_stage;
  /** The current submission */
  submission = this._submissionDetailStore.$submission;

  readonly $submission = signal<SubmissionResponse | null>(null);
  /** Loading state of the submission */
  $isLoading = this._submissionDetailStore.$isLoading;
  $error = this._submissionDetailStore.$error;
  readonly $isInternal = toSignal(this._permissionsService.isInternal$);
  readonly $user$ = toSignal(this._authService.user$);

  $hasOutcomeDocuments = this._submissionDetailStore.$hasOutcomeDocuments;
  $sites = this._submissionDetailStore.$sites;
  $organiationStatus = this._submissionDetailStore.$organiationStatus;
  $reloadContactOrgs = this._submissionDetailStore.$reloadContactOrgs;

  $organizationValidationAssignedTypes =
    this._submissionDetailStore.$organizationValidationAssignedTypes;
  $organizationValidationUnassignedTypes =
    this._submissionDetailStore.$organizationValidationUnassignedTypes;

  // $sites = this._submissionDetailStore.$sites;

  loadSites(submissionId: string, queryParams?: HttpParams) {
    this._submissionDetailStore.loadSubmissionSites(submissionId, queryParams);
  }

  private readonly _canRemoveSignal = signal<boolean | null>(null);
  readonly $showTasksDetails = signal<boolean>(environment.enabledTask);
  /** Signals */

  /** Computed Signals */
  readonly $canEdit = computed(() =>
    this._getSubmissionPermissions(
      [SiteSubmission, StudySubmission],
      [PermissionGrants.CanEdit],
    ),
  );
  readonly $canRemove = computed(() => {
    const submission = this.$submission();
    if (!submission) {
      return null;
    }
    return this._canRemoveSignal();
  });

  readonly $canReassign = computed(() =>
    this._getSubmissionPermissions(
      [ReassignSubmission],
      [PermissionGrants.CanEdit],
    ),
  );

  readonly $canViewSubmissionSites = computed(() =>
    this._getSubmissionPermissions(
      [SiteSubmission, StudySubmission],
      [PermissionGrants.CanView],
    ),
  );

  readonly $canEditSubmissionDetails = computed(() =>
    this._getSubmissionPermissions(
      [QuestionnaireAccess],
      [PermissionGrants.CanEdit],
    ),
  );

  readonly $canViewSubmissionDocuments = computed(() =>
    this._getSubmissionPermissions(
      [SubmissionDocumentAccess],
      [PermissionGrants.CanView],
    ),
  );

  readonly $canUpdateSubmissionPriority = computed(() =>
    this._getSubmissionPermissions(
      [UpdateSubmissionPriority],
      [PermissionGrants.CanEdit],
    ),
  );

  readonly $canUpdateSubmissionTeam = computed(() =>
    this._getSubmissionPermissions(
      [UpdateSubmissionTeam],
      [PermissionGrants.CanEdit],
    ),
  );

  readonly $isQC = computed(() => this._isUserSMEQC('qc'));
  readonly $isSME = computed(() => this._isUserSMEQC('sme'));
  /** Computed Signals */

  constructor() {
    // Watch for submission changes and update canRemove signal using observables
    toObservable(this.$submission)
      .pipe(takeUntilDestroyed())
      .subscribe((submission) => {
        if (submission) {
          this._updateCanRemoveSignal(submission);
        }
      });

    toObservable(this.submission)
      .pipe(takeUntilDestroyed())
      .subscribe((submission) => {
        if (submission?.id) {
          this.$submission.set(submission);
        }
      });
  }

  getSubmissionDetail(submissionId: string) {
    return this._submissionDetailStore.loadSubmission(submissionId);
  }
  updateSubmission(submission: SubmissionResponse) {
    this._submissionDetailStore.updateSubmission(submission);
    this._updateCanRemoveSignal(submission);
  }

  resetSubmission() {
    this._submissionDetailStore.resetState();
  }
  /**
   * Delete a submission.
   * @param workspaceId - The ID of the workspace to delete.
   * @param reason_for_delete - The reason for deleting the submission.
   * @param isInternal - Whether the user is internal.
   * @returns An Observable that completes when the deletion is done.
   */
  deleteSubmission(
    workspaceId: string,
    reason_for_delete: string,
    isInternal: boolean,
  ) {
    return this._submissionDetailStore.deleteSubmission(
      workspaceId,
      reason_for_delete,
      isInternal,
    );
  }

  /*
   * A private method to check if the user has the required permissions for a submission.
   * @param permissions - An array of BasePermission types to check against.
   * @param grants - An array of PermissionGrants that the user must have.
   * @return A boolean indicating if the user has all the required permissions, or null if no submission is found.
   */
  private _getSubmissionPermissions(
    permissions: BasePermission[],
    grants: PermissionGrants[],
  ) {
    const submission = this.$submission();
    if (!submission) {
      return null;
    }
    const submissionRole: UserRole = convertKeysToCamelCase(
      submission.role,
    ) as UserRole;

    // Check if the user has all the required grants
    return grants.every((userGrant: PermissionGrants) => {
      const formattedUserGrant = convertSnakeToCamelCase(userGrant);
      // Check if the formatted permission exists in the role's permission,
      // and returns true if found
      if (submissionRole?.permissions) {
        return submissionRole.permissions.some(
          (perm) => perm[formattedUserGrant as keyof typeof perm],
        );
      }
      // If no permissions are found, return false
      return false;
    });
  }

  /**
   * A private method to check if the current user is in a specific role (qc or sme) for the submission.
   * @param role - The role to check ('qc' or 'sme').
   * @return A boolean indicating if the user is an SME or QC for the submission
   */
  private _isUserSMEQC(role: 'qc' | 'sme'): boolean {
    const submission = this.$submission();
    const user = this.$user$();

    if (!submission) {
      return false;
    }
    if (!user) {
      return false;
    }

    return submission[role]?.id === user.id;
  }

  /*
   * * A method to update the submission stage.
   * @param workspaceID - The ID of the workspace associated with the submission.
   */
  updateSubmissionStage(payload: UpdateSubmissionStagePayload) {
    this._submissionDetailStore.updateSubmissionStage(payload);
  }

  organizationValidation(workspaceID: string) {
    this._submissionDetailStore.organizationValidation(workspaceID);
  }

  /*
   * A method to complete an IBC submission.
   * @param workspaceID - The ID of the workspace to complete the IBC submission for.
   */
  completeIbcSubmission(workspaceID: string) {
    this._submissionDetailStore.completeIbcSubmission(workspaceID);
  }

  /*
   * A method to open a dialog for updating the submission name.
   * @param submissionName - The current name of the submission to be edited.
   */
  updateSubmssionName(submissionName: string): void {
    const dialogPayload: OpenDialogState = {
      component: () => WcgSingleTextDialogComponent,
      componentData: {
        data: submissionName,
        dialogType: DIALOG_TYPE.SingleText,
        errorText: 'Submission Name is required',
        isRequired: true,
        label: 'Submission Name',
        mode: DIALOG_MODES.EDITNAME,
        propKey: 'name',
        size: DialogSize.MEDIUM,
        title: 'Edit Submission Name',
        validatorFn: () => Validators.required,
      },
      type: DIALOG_TYPE.SingleText,
    };

    this._editNameDialog(dialogPayload);
  }

  /**
   * Private method to handle the edit name dialog logic
   * @param payload - The dialog configuration payload
   */
  private _editNameDialog(payload: OpenDialogState): void {
    const dialogRef = this._submissionDetailStore.openDialog(payload);
    if (dialogRef) {
      dialogRef
        .afterClosed()
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          error: (err: Error) => {
            console.error('Error handling dialog result:', err);
          },
          next: (data: { value: string; mode: string; index?: number }) => {
            this._updateSubmissionName(data.value);
          },
        });
    }
  }

  /**
   * Private method to update the submission name via signal store
   * @param value - The new name value
   */
  private _updateSubmissionName(value: string): void {
    const submission = this.$submission();
    if (!submission?.id) {
      console.error('No submission ID available for name update');
      return;
    }

    // Use the signal store method to handle the API call and state update
    this._submissionDetailStore.updateSubmissionName(submission.id, value);
  }

  private _updateCanRemoveSignal(submission: SubmissionResponse) {
    const canRemove = combineLatest([
      // Check remove permission for the submission
      this._permissionsService.observeEntityPermissions(
        submission.id,
        [StudySubmission, SiteSubmission],
        [PermissionGrants.CanRemove],
      ),
      // Check user permissions
      this._permissionsService.observeUserPermissions(
        [SubmissionSuperAdmin],
        [PermissionGrants.CanRemove],
      ),

      // Check if the user is internal
      this._permissionsService.isInternal$,
    ]).pipe(
      map(([removePermission, userPermissions, isInternal]) => {
        // IF the user has submission super admin permission, they can always delete the submission
        if (userPermissions) {
          return true;
        }

        // If the submission is in draft stage and the user has edit or remove permission, they can delete the submission
        if (submission.current_stage === 'draft' && removePermission) {
          return true;
        }

        const grantedPermissions = submission.role?.permissions?.some((p) => {
          const canRemove = 'can_remove' in p ? p.can_remove : p.canRemove;
          return canRemove;
        });

        if (submission.current_stage === 'draft' && grantedPermissions) {
          return true;
        }

        // If the submission is in qc_check stage and the user is internal with remove permission, they can delete the submission
        if (
          submission.current_stage === 'qc_check' &&
          isInternal &&
          (removePermission || userPermissions)
        ) {
          return true;
        }

        return false;
      }),
    );

    canRemove
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((canRemoveValue: boolean) =>
        this._canRemoveSignal.set(canRemoveValue),
      );
  }

  updateSubmissionSites(data: SubmissionSite[], total: number) {
    this._submissionDetailStore.updateSubmissionSites(data, total);
  }

  /**
   * Trigger loading the appeal submission setup path.
   * Path is set in $appealSubmissionSetupPath; caller should navigate then call clearAppealSubmissionSetupPath.
   * @param submissionId - The ID of the submission to create an appeal for
   */
  openAppealSubmissionSetup(submissionId: string) {
    this._submissionDetailStore.openAppealSubmissionSetup(submissionId);
  }

  /** Clear the appeal submission setup path after navigation */
  clearAppealSubmissionSetupPath() {
    this._submissionDetailStore.clearAppealSubmissionSetupPath();
  }

  /** Path to navigate for appeal submission setup (set by openAppealSubmissionSetup) */
  get $appealSubmissionSetupPath() {
    return this._submissionDetailStore.$appealSubmissionSetupPath;
  }
}
